import type { Time, TimeRange } from "@adrekic/fieldsjson"
import { isTime, isTimeRange, TimeField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	TimeAfterFilterModifier,
	TimeBeforeFilterModifier,
	TimeEqualsFilterModifier,
	TimeNotEqualsFilterModifier,
	TimeRangeInFilterModifier,
	TimeRangeNotInFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { TimeFieldFilterCell } from "./TimeFieldFilterCell"

type TimeFieldValue = Time | null
type TimeFieldFilterValue = Time | TimeRange

export class TimeFieldFilterDef extends BaseFilterDef<TimeFieldValue, TimeFieldFilterValue> {
	readonly id: string
	readonly defaultFilterValue: TimeFieldFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly field: TimeField

	constructor(field: TimeField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: TimeEqualsFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is Time => isTime(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: TimeNotEqualsFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is Time => isTime(filterValue),
		}),
		before: createFilterModifierConfig({
			modifier: TimeBeforeFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is Time => isTime(filterValue),
		}),
		after: createFilterModifierConfig({
			modifier: TimeAfterFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is Time => isTime(filterValue),
		}),
		in: createFilterModifierConfig({
			modifier: TimeRangeInFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is TimeRange =>
				isTimeRange(filterValue),
		}),
		notIn: createFilterModifierConfig({
			modifier: TimeRangeNotInFilterModifier,
			isValueValid: (value: TimeFieldValue): value is Time => isTime(value),
			isFilterValueValid: (filterValue: TimeFieldFilterValue): filterValue is TimeRange =>
				isTimeRange(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<TimeFieldFilterDef>): ReactNode {
		return <TimeFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
